import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  HStack,
  Button,
  Text,
  IconButton,
  VStack,
  Tooltip,
  Menu
} from '@chakra-ui/react'
import {
  DrawerRoot,
  DrawerBackdrop,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger
} from '@chakra-ui/react'
import { FiMenu, FiX, FiGrid, FiChevronDown } from 'react-icons/fi'
import { Logo } from '../Brand/Logo'
import { useTranslation } from 'react-i18next'
import { auth } from '../../config/firebase'
import { onAuthStateChanged } from 'firebase/auth'

export const SpotlightHeader: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { t } = useTranslation()

  // Check authentication status and admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAuthenticated(!!user)
      if (user) {
        try {
          const token = await user.getIdTokenResult()
          setIsAdmin(token.claims.admin === true)
        } catch (error) {
          console.error('Failed to get token:', error)
          setIsAdmin(false)
        }
      } else {
        setIsAdmin(false)
      }
    })
    return () => unsubscribe()
  }, [])
  
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const navLinks = [
    { path: '/', label: t('navigation.overview'), tooltip: 'Learn about Spotlight' },
    { path: '/how-it-works', label: t('navigation.how_it_works'), tooltip: 'See how it works' },
    { path: '/for-services', label: t('navigation.for_services'), tooltip: 'For service providers' },
    { path: '/for-products', label: t('navigation.for_products'), tooltip: 'For product sellers' },
    { path: '/pricing', label: t('navigation.pricing'), tooltip: 'View pricing plans' }
  ]

  const getStartedPath =
    location.pathname.startsWith('/for-products')
      ? '/for-products/get-started'
      : location.pathname.startsWith('/for-services') || location.pathname.startsWith('/for-practitioners')
        ? '/for-services/get-started'
        : '/get-started'

  const isOnGetStartedPage = location.pathname.endsWith('/get-started')

  const handleGetStartedClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (isOnGetStartedPage) {
      if (isAuthenticated) {
        // Navigate to onboarding
        const defaultOnboardingPath =
          location.pathname.startsWith('/for-products')
            ? '/for-products/onboarding/basic-setup'
            : location.pathname.startsWith('/for-services')
              ? '/for-services/onboarding/basic-setup'
              : '/for-services/onboarding/basic-setup'
        navigate(defaultOnboardingPath)
      } else {
        // Scroll to "Ready to get started?" section
        const section = document.getElementById('ready-to-get-started')
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    } else {
      navigate(getStartedPath)
    }
  }

  return (
    <Box
      as="nav"
      bg="rgba(255, 255, 255, 0.95)"
      borderBottom="1px solid"
      borderColor="gray.100"
      position="sticky"
      top={0}
      zIndex={1000}
      backdropFilter="blur(20px)"
      py={2}
    >
      <Container maxW="7xl">
        <HStack justify="space-between" py={2} gap={8}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <HStack gap={3} align="center">
              <Logo size="md" />
              <Box height="24px" width="1px" bg="gray.300" transform="rotate(15deg)" />
              <Text fontSize="xl" fontWeight="medium" color="gray.500" letterSpacing="tight" mt={1}>
                Spotlight
              </Text>
            </HStack>
          </Link>

          {/* Navigation Links - Desktop */}
          <HStack gap={8} display={{ base: 'none', md: 'flex' }}>
            {navLinks.map((link) => {
              const active = link.path === '/'
                ? location.pathname === '/'
                : (link.path === '/for-services'
                  ? (location.pathname === '/for-services' || location.pathname === '/for-practitioners')
                  : isActive(link.path))
              return (
                <Tooltip.Root key={link.path}>
                  <Tooltip.Trigger asChild>
                    <Link to={link.path} style={{ textDecoration: 'none' }}>
                      <Text
                        fontSize="md"
                        fontWeight={active ? 'bold' : 'medium'}
                        color={active ? 'brand.700' : 'gray.600'}
                        _hover={{ color: 'brand.700' }}
                        transition="color 0.2s"
                      >
                        {link.label}
                      </Text>
                    </Link>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content
                      bg="gray.900"
                      color="white"
                      px={3}
                      py={2}
                      borderRadius="md"
                      fontSize="sm"
                    >
                      <Tooltip.Arrow>
                        <Tooltip.ArrowTip fill="gray.900" />
                      </Tooltip.Arrow>
                      {link.tooltip}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              )
            })}
          </HStack>

          {/* Mobile Menu Button with Tooltip */}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <IconButton
                display={{ base: 'flex', md: 'none' }}
                aria-label="Open menu"
                icon={<FiMenu />}
                variant="ghost"
                onClick={() => setIsMobileMenuOpen(true)}
                size="lg"
              />
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content
                bg="gray.900"
                color="white"
                px={3}
                py={2}
                borderRadius="md"
                fontSize="sm"
              >
                <Tooltip.Arrow>
                  <Tooltip.ArrowTip fill="gray.900" />
                </Tooltip.Arrow>
                Open menu
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>

          {/* Dashboard + CTA Buttons - Desktop */}
          <HStack gap={3} display={{ base: 'none', md: 'flex' }}>
            {isAuthenticated && (
              isAdmin ? (
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      colorPalette="brand"
                      size="lg"
                      fontWeight="medium"
                      px={4}
                      rightIcon={<FiChevronDown />}
                      leftIcon={<FiGrid />}
                    >
                      Dashboard
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item value="campaigns" onClick={() => navigate('/dashboard')}>
                        Campaigns Dashboard
                      </Menu.Item>
                      <Menu.Item value="admin" onClick={() => navigate('/admin/overview')}>
                        Admin Dashboard
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              ) : (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Button
                      as={Link}
                      to="/dashboard"
                      variant="outline"
                      colorPalette="brand"
                      size="lg"
                      fontWeight="medium"
                      px={4}
                      leftIcon={<FiGrid />}
                    >
                      Dashboard
                    </Button>
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content
                      bg="gray.900"
                      color="white"
                      px={3}
                      py={2}
                      borderRadius="md"
                      fontSize="sm"
                    >
                      <Tooltip.Arrow>
                        <Tooltip.ArrowTip fill="gray.900" />
                      </Tooltip.Arrow>
                      View your campaigns
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              )
            )}
            <Button
              onClick={handleGetStartedClick}
              colorPalette="brand"
              size="lg"
              fontWeight="bold"
              color="white"
              px={6}
              _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
              transition="all 0.2s"
            >
              {t('common.get_started')}
            </Button>
          </HStack>
        </HStack>
      </Container>

      {/* Mobile Drawer */}
      <DrawerRoot
        open={isMobileMenuOpen}
        onOpenChange={(details) => setIsMobileMenuOpen(details.open)}
        placement="end"
        size="sm"
      >
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px" py={4}>
            <HStack justify="space-between" w="full">
              <HStack gap={2} align="center">
                <Logo size="sm" />
                <Text fontSize="lg" fontWeight="medium" color="gray.500">
                  Spotlight
                </Text>
              </HStack>
              <DrawerCloseTrigger asChild>
                <IconButton
                  aria-label="Close menu"
                  icon={<FiX />}
                  variant="ghost"
                  size="sm"
                />
              </DrawerCloseTrigger>
            </HStack>
          </DrawerHeader>
          <DrawerBody py={6}>
            <VStack align="stretch" gap={3}>
              {navLinks.map((link) => {
                const active = link.path === '/'
                  ? location.pathname === '/'
                  : (link.path === '/for-services'
                    ? (location.pathname === '/for-services' || location.pathname === '/for-practitioners')
                    : isActive(link.path))
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    style={{ textDecoration: 'none' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Box
                      py={3}
                      px={4}
                      borderRadius="lg"
                      bg={active ? 'brand.50' : 'transparent'}
                      borderWidth="1px"
                      borderColor={active ? 'brand.200' : 'transparent'}
                      _hover={{
                        bg: active ? 'brand.100' : 'gray.50',
                        borderColor: active ? 'brand.300' : 'gray.200',
                        transform: 'translateX(4px)'
                      }}
                      transition="all 0.2s"
                    >
                      <Text
                        fontSize="md"
                        fontWeight={active ? 'semibold' : 'medium'}
                        color={active ? 'brand.700' : 'gray.700'}
                      >
                        {link.label}
                      </Text>
                    </Box>
                  </Link>
                )
              })}
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" py={6}>
            <VStack align="stretch" w="full" gap={3}>
              {isAuthenticated && (
                isAdmin ? (
                  <VStack align="stretch" w="full" gap={2}>
                    <Button
                      variant="outline"
                      colorPalette="brand"
                      size="lg"
                      fontWeight="medium"
                      w="full"
                      leftIcon={<FiGrid />}
                      onClick={() => {
                        navigate('/dashboard')
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      Campaigns Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      colorPalette="brand"
                      size="lg"
                      fontWeight="medium"
                      w="full"
                      leftIcon={<FiGrid />}
                      onClick={() => {
                        navigate('/admin/overview')
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      Admin Dashboard
                    </Button>
                  </VStack>
                ) : (
                  <Button
                    as={Link}
                    to="/dashboard"
                    variant="outline"
                    colorPalette="brand"
                    size="lg"
                    fontWeight="medium"
                    w="full"
                    leftIcon={<FiGrid />}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Button>
                )
              )}
              <Button
                onClick={(e) => {
                  handleGetStartedClick(e)
                  setIsMobileMenuOpen(false)
                }}
                colorPalette="brand"
                size="lg"
                fontWeight="bold"
                w="full"
              >
                {t('common.get_started')}
              </Button>
            </VStack>
          </DrawerFooter>
        </DrawerContent>
      </DrawerRoot>
    </Box>
  )
}
